import React, { useEffect } from "react";
import { Box } from "@mui/material";
import FormField from "../../../../../components/FormField";
import LocationIdFilter from "../../../../../components/filters/LocationIdFilter";
import OccupantFilter from "../../../../../components/filters/OccupantFilter";
import "../index.css";
import { icons } from "../../../../../svg";
import { useNitroCAStore } from "../../../../../store/CompetitiveAnalysisStore";

interface Location {
  id: string;
  name: string;
  existsInRoutes?: boolean;
}

interface FindMissingOriginProps {
  origin: Location;
  setOrigin: (value: Location) => void;
  occupant: Location;
  setOccupant: (value: Location) => void;
}

const FindMissingOrigin: React.FC<FindMissingOriginProps> = ({
  origin,
  setOrigin,
  occupant,
  setOccupant,
}) => {
  const [kochRecords, competitorRecords] = useNitroCAStore((state) => [
    state.kochRecords,
    state.competitorRecords,
  ]);

  const handleSetOrigin = (value: Location) => {
    setOrigin({
      ...value,
    });
  };

  const handleSetOccupantList = (value: any[]) => {
    if (value.length === 2) {
      const recordsToSearch = [...kochRecords, ...competitorRecords];

      const originExists = recordsToSearch.some(
        (record) => record.origin.id === origin.id
      );

      setOrigin({
        ...origin,
        existsInRoutes: originExists,
      });
    }
  };

  useEffect(() => {
    if (occupant.id) {
      const isKoch = occupant.name.toLowerCase().includes("koch");
      const recordsToSearch = isKoch ? kochRecords : competitorRecords;

      const originExists = recordsToSearch.some(
        (record) => record.origin.id === origin.id
      );

      setOrigin({
        ...origin,
        existsInRoutes: originExists,
      });
    }
  }, [competitorRecords, kochRecords, occupant, origin, setOrigin]);
  return (
    <div className="find-missing-origin-container">
      <div className="find-missing-origin-header">Lookup Facility Name</div>
      <div className="find-missing-origin-header-content">
        Select a Koch or competitor origin to explore why it is missing.
      </div>

      <div className="search-tip-container">
        <div className="search-tip-header-row">
          <span>
            <img
              data-tooltip-id="light-bulb"
              src={icons.LightBulb}
              alt="light-bulb"
              className="light-bulb"
            />
          </span>
          <span className="search-tip-header">Search Tips:</span>
        </div>

        <div className="search-tip-content">
          <ul style={{ margin: "0", paddingLeft: "20px" }}>
            <li>
              Start by typing the exact or partial facility name; incorrect
              names will show no results
            </li>
            <li>
              Only existing facilities will display; if you are sure the
              location doesn’t exist, add it
            </li>
            <li>
              Selecting an origin, such as Inola, with multiple occupants
              requires you to choose a specific occupant at that location.
            </li>
          </ul>
        </div>
      </div>

      {/* --- Existing Form Fields --- */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Origin Field */}
        <FormField label="Facility Name">
          <LocationIdFilter
            value={origin.name}
            setValue={(value: Location) => {
              handleSetOrigin(value);
              handleSetOccupantList([]);
            }}
            isFavoriteOrigins={false}
            config={{ valueRequired: true }}
          />
        </FormField>

        {/* Occupant Field */}
        <FormField label="Occupant Name">
          <OccupantFilter
            value={occupant}
            locationId={origin.id}
            disabled={!origin.id}
            setValue={setOccupant}
            setOccupantList={handleSetOccupantList}
            config={{ valueRequired: true }}
          />
        </FormField>
      </Box>
    </div>
  );
};

export default FindMissingOrigin;
